import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import axios, { CanceledError, HttpStatusCode } from "axios";
import LoadingButton from "_shared/_views/loading/loading-button";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import downloadFile from "_shared/_utilities/download-file";
import Guid from "_shared/guids/guid";
import DocumentQuery, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters,
} from "documents/_api/queries/document-query";
import Document from "documents/document";
import DocumentTopic from "documents/topics/document-topic";
import {
  createMRTColumnHelper,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  type MRT_TableOptions,
  type MRT_TableInstance,
  type MRT_Row,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo } from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import { useSession } from "users/__session/session-context";
import DocumentAPIService from "documents/_api/document-api-service";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import { DocumentFileType } from "documents/types/document-file-type";
import { MRTNoMemo } from "_shared/_views/tables/table";

const FileName = styled(Typography)(() => ({
  fontSize: "1em",
  fontWeight: "500",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NameContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "flex",
  maxWidth: "20rem",
  paddingLeft: theme.spacing(2),
  svg: {
    width: "24px",
  },
}));
const ToolbarButtonsContainer = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexDirection: "row",
}));
const RowActions = styled("div")(() => ({
  display: "flex",
}));

type DocumentSelectorProps = {
  isLoading?: boolean;
  documentType: "conflicts" | "policy";
  selectedTab?: "templates" | "documents";
  selectedDocumentIds: Guid[];
  selectedTemplateIds?: Guid[];
  onDocumentSelectionChanged: (selectedDocs: Document[], deselectedDocs: Document[]) => void;
  onCreateDocumentFromTemplate: (templateId: Guid) => void;
};

const columnHelper = createMRTColumnHelper<Document>();

export default function DocumentSelector(props: Readonly<DocumentSelectorProps>) {
  const {
    documentType,
    selectedTab,
    selectedDocumentIds,
    selectedTemplateIds,
    onDocumentSelectionChanged,
    onCreateDocumentFromTemplate,
  } = props;

  const documentFileUploaderRef = React.useRef<HTMLInputElement | null>(null);
  const skipNextSelectionEffect = React.useRef(false);

  const [isLoading, setIsLoading] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const [errorLoading, setErrorLoading] = React.useState(false);
  const [rowCount, setRowCount] = React.useState(0);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [activeTab, setActiveTab] = React.useState<"templates" | "documents">(selectedTab ?? "templates");
  const [availableDocuments, setAvailableDocuments] = React.useState<Document[]>([]);

  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});

  const theme = useTheme();
  const session = useSession();

  const columns = useMemo(() => [
    columnHelper.display({
      header: "Name",
      id: "name",
      Cell: NameCell
    })
  ], []);

  const fetchData = useCallback(async (abortController?: AbortController) => {
    if (availableDocuments.length) {
      setIsRefetching(true);
    } else {
      setIsLoading(true);
    }

    try {
      const apiService = new DocumentAPIService(session);
      const paginationParameters = PaginationParameters.create(pagination);
      const order = sorting.find((sort) => sort !== undefined);
      const orderParameters = new DocumentOrderParameters({
        orderBy: order?.id === "name" ? DocumentOrderField.Name : DocumentOrderField.Created,
        orderDir: order?.desc ? OrderDirection.Descending : OrderDirection.Ascending,
      });
      const filterParameters = new DocumentFilterParameters({
        entityClass: activeTab === "documents" ? "Work.Proposal" : undefined,
        name: columnFilters.find((filter) => filter.id === "name")?.value?.toString() ?? globalFilter,
        contexts: [documentType + (activeTab === "templates" ? "template" : "")],
      });
      const documentQuery = new DocumentQuery({
        orderParameters,
        filterParameters,
        paginationParameters,
      });
      const paginatedResponse = await apiService.getDocumentsInfo(documentQuery, abortController);

      setAvailableDocuments(paginatedResponse.data);
      setRowCount(paginatedResponse.totalElements);
    } catch (error) {
      setErrorLoading(true);
      if (error instanceof CanceledError) return;
      console.error(error);
      return;
    }
    setErrorLoading(false);
    setIsLoading(false);
    setIsRefetching(false);
  }, [
    availableDocuments.length, 
    session, pagination, 
    sorting, 
    activeTab, 
    columnFilters, 
    globalFilter, 
    documentType
  ]);

  useEffect(() => {
    let abortController = new AbortController();
    void fetchData(abortController);
    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, [
    activeTab, 
    session, 
    columnFilters, 
    globalFilter, 
    pagination.pageIndex, 
    pagination.pageSize, 
    sorting, 
    fetchData
  ]);

  useEffect(() => {
    const updatedRowSelection: Record<string, boolean> = {};

    for (const id of selectedDocumentIds) {
      updatedRowSelection[id.value] = true;
    }

    setRowSelection((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(updatedRowSelection);

      const sameLength = prevKeys.length === nextKeys.length;
      const sameValues = sameLength && prevKeys.every((key) => prev[key] === updatedRowSelection[key]);

      if (sameValues) return prev;

      skipNextSelectionEffect.current = true;
      return updatedRowSelection;
    });
  }, [selectedDocumentIds]);

  useEffect(() => {
    if (skipNextSelectionEffect.current) {
      skipNextSelectionEffect.current = false;
      return;
    }

    const selectedIds: Guid[] = [];

    for (const [idString, isSelected] of Object.entries(rowSelection)) {
      if (isSelected) {
        const id = Guid.fromJSON(idString);
        if (id) selectedIds.push(id);
      }
    }

    const selectedDocs = availableDocuments.filter((doc) =>
      selectedIds.some((id) => doc.id.isEqualTo(id))
    );

    const deselectedDocs = availableDocuments.filter(
      (doc) => !selectedIds.some((id) => doc.id.isEqualTo(id))
    );

    if (
      selectedDocs.some((doc) => !selectedDocumentIds.some((id) => doc.id.isEqualTo(id))) ||
      deselectedDocs.some((doc) => selectedDocumentIds.some((id) => doc.id.isEqualTo(id)))
    ) {
      onDocumentSelectionChanged(selectedDocs, deselectedDocs);
    }
  }, [availableDocuments, onDocumentSelectionChanged, rowSelection, selectedDocumentIds]);

  const handleDownload = useCallback(async (id: Guid): Promise<void> => {
    try {
      if (!id) {
        await Promise.reject(new Error("No document id provided"));
        return;
      }
      const service = new DocumentAPIService(session);
      const response = await service.downloadDocument(id);
      await downloadFile(response);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to download file", { variant: "error" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.user?.id?.value]);

  async function handleDocumentFileUpload(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    if (!event.currentTarget.files) return;

    const file: File = event.currentTarget.files[0];

    try {
      setIsUploading(true);

      let context: string | undefined;
      context = documentType === "conflicts" ? "Conflicts" : "Policy";
      if (activeTab === "templates") {
        context = context.concat("Template");
      }
      const service = new DocumentAPIService(session);
      await service.createDocument(file, file.name, [DocumentTopic.create({ context })]);
      enqueueSnackbar("Uploaded file", { variant: "success" });

      await fetchData();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        if (
          error.response.status === HttpStatusCode.UnsupportedMediaType ||
          error.response.status === HttpStatusCode.UnprocessableEntity
        ) {
          enqueueSnackbar("Unsupported file type", { variant: "error" });
        } else {
          enqueueSnackbar(error.response.data, { variant: "error" });
        }
      } else {
        enqueueSnackbar("Unknown error occurred during upload", {
          variant: "error",
        });
      }
    } finally {
      event.target.value = "";
      setIsUploading(false);
    }
  }

  const getRowStyle = useCallback((rowData: Document): React.CSSProperties => {
    if (rowData.id === undefined) return {};
    const color = selectedDocumentIds.some((id) => rowData.id.isEqualTo(id)) ? "rgba(0, 0, 0, 0.5)" : "inherit";
    let bgColor = selectedTemplateIds?.some((id) => rowData.id.isEqualTo(id)) ? "rgba(0, 200, 0, 0.1)" : "inherit";
    if (selectedDocumentIds.some((id) => rowData.id.isEqualTo(id))) {
      bgColor = theme.palette.primary.light;
    }

    return {
      backgroundColor: bgColor,
      color: color,
    };
  }, [selectedDocumentIds, selectedTemplateIds, theme]);

  const renderToolbarInternalActions = useCallback(() => (
    <ToolbarButtonsContainer>
      <LoadingButton
        color="primary"
        loading={isUploading}
        startIcon={<UploadIcon />}
        onClick={() => documentFileUploaderRef.current?.click()}
      >
        Upload Template
      </LoadingButton>
    </ToolbarButtonsContainer>
  ), [isUploading]);

  const renderTopToolbarCustomActions = useCallback(() => (
    <Tabs
      value={activeTab}
      onChange={(_event, value) => {
        setActiveTab(value);
      }}
      indicatorColor="primary"
      textColor="primary"
      variant="fullWidth"
    >
      <Tab label="Templates" value="templates" />
      <Tab label="Recent" value="documents" />
    </Tabs>
  ), [activeTab]);

  const renderRowActions = useCallback((params: { row: MRT_Row<Document> }) => {
    const { row } = params;
    return (
      <RowActions>
        <Tooltip title="Create Document from Template">
          <span>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                onCreateDocumentFromTemplate(row.original.id);
              }}
            >
              <ContentPasteGoIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Download">
          <span>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                void handleDownload(row.original.id);
              }}
            >
              <DownloadIcon color="primary" />
            </IconButton>
          </span>
        </Tooltip>
      </RowActions>
    );
  }, [handleDownload, onCreateDocumentFromTemplate]);

  const muiTableBodyRowProps = useCallback(({ row }: { row: MRT_Row<Document> }) => ({
    onClick: () =>
      setRowSelection((prev) => ({
        ...prev,
        [row.id]: !prev[row.id],
      })),
    selected: rowSelection[row.id],
    sx: {
      backgroundColor: getRowStyle(row.original).backgroundColor,
      color: getRowStyle(row.original).color,
      cursor: "pointer",
    },
  }), [rowSelection, getRowStyle]);

  const muiSelectCheckboxProps = useCallback(({ row }: { row: MRT_Row<Document> }) => ({
    checked: rowSelection[row.id] ?? false,
    indeterminate:
      (!selectedDocumentIds?.some((id) => row.original.id.isEqualTo(id)) &&
        selectedTemplateIds?.some((id) => row.original.id.isEqualTo(id))) ||
      false,
  }), [rowSelection, selectedDocumentIds, selectedTemplateIds]);

  const muiSelectAllCheckboxProps = useCallback(({ table }: { table: MRT_TableInstance<Document> }) => ({
    slotProps: {
      root: {
        component: FormControlLabel,
        control: (
          <Checkbox
            disableRipple={true}
            size={"small"}
            checked={table.getIsAllRowsSelected() ?? false}
            indeterminate={table.getIsSomeRowsSelected() ?? false}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        label: "All",
      },
    },
  }), []);

  const tableOptions: MRT_TableOptions<Document> = {
    columns,
    data: availableDocuments,
    enableSorting: true,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: true,
    enableTableHead: true,
    initialState: {
      showGlobalFilter: true,
      density: "compact",
      columnPinning: {
        right: ["mrt-row-actions"],
      },
    },
    positionToolbarAlertBanner: "none",
    muiTopToolbarProps: {
      sx: {
        containerType: "inline-size",
        "& > .MuiBox-root": {
          "@container (min-width: 600px)": {
            alignItems: "end",
            flexDirection: "row",
            mt: 0
          },
          mt: 1,
          flexDirection: "column-reverse",
          paddingTop: 0,
          width: "100%"
        },
        width: "100%"
      },
    },
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      showRowsPerPage: false,
      variant: 'outlined',
    },
    paginationDisplayMode: 'pages',
    renderToolbarInternalActions,
    renderTopToolbarCustomActions,
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    renderRowActions,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: {
      sx: {
        flex: 1
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%"
      },
    },
    muiToolbarAlertBannerProps: errorLoading
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    muiTableBodyRowProps,
    muiSelectCheckboxProps,
    muiSelectAllCheckboxProps,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    rowCount,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: errorLoading,
      showProgressBars: isRefetching,
      sorting,
      rowSelection,
    },
  }

  return (
    <>
      <input
        type="file"
        hidden={true}
        ref={documentFileUploaderRef}
        onChange={handleDocumentFileUpload}
      />
      <MRTNoMemo<Document> options={tableOptions} />
    </>
  );
}

function NameCell({ row }: Readonly<{ row: MRT_Row<Document> }>) {
  const document = row.original;
  const fileType = enumKeyOf(DocumentFileType, document.fileType?.toString()).toLocaleLowerCase();
  return (
    <NameContainer>
      <FileIcon
        extension={fileType}
        {...defaultStyles[fileType as keyof typeof defaultStyles]}
      />
      <FileName>{document.name?.value}</FileName>
    </NameContainer>
  );
}
